import React, { useState, useRef, useEffect } from 'react';
import Icons from '../../../../../components/Common/Icons/Icons';
import { Button } from '../../../../../components/Common/UI/Button';

export interface BatchDownloadResult {
  blob: Blob;
  filename: string;
}

interface BatchProcessingNotificationProps {
  onCompress: () => Promise<BatchDownloadResult>;
  disabled: boolean;
  className?: string;
  onScrollTop?: () => void;
}

function AnimatedDots() {
  const [dots, setDots] = useState('');

  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => (prev.length >= 3 ? '' : `${prev}.`));
    }, 500);

    return () => clearInterval(interval);
  }, []);

  return <span>{dots}</span>;
}

function BatchProcessingNotification({
  onCompress,
  onScrollTop,
  disabled,
  className,
}: BatchProcessingNotificationProps) {
  const [status, setStatus] = useState<'idle' | 'processing' | 'success'>('idle');
  const [downloadResult, setDownloadResult] = useState<BatchDownloadResult | null>(null);
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const handleCompressClick = async () => {
    if (disabled) return;

    setStatus('processing');
    setVisible(true);
    requestAnimationFrame(() => {
      onScrollTop?.();
    });

    try {
      const result = await onCompress();

      setDownloadResult(result);

      timerRef.current = setTimeout(() => setStatus('success'), 3000);

      setTimeout(() => {
        setVisible(false);
        setStatus('idle');
      }, 10000);
    } catch {
      setStatus('idle');
      setVisible(false);
      alert('Error during batch processing');
    }
  };

  const handleDownload = () => {
    if (!downloadResult) return;

    const url = URL.createObjectURL(downloadResult.blob);
    const a = document.createElement('a');

    a.href = url;
    a.download = downloadResult.filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <Button
        className={
          className ||
          'px-[20px] py-[3px] xs:h-[32px] h-[38px] !bg-primary !text-white hover:opacity-80 xs:w-auto w-full'
        }
        onClick={handleCompressClick}
        disabled={disabled || status === 'processing'}
      >
        <Icons.ZIP />
        Compress into Zip
      </Button>

      {visible && (
        <div
          className={`
      fixed top-[40px] left-1/2 z-50 w-[202px] rounded
      shadow-[0_8px_30px_rgba(0,0,0,0.4)]
      px-4 py-3
      text-white text-[12px] select-none
      ${status === 'processing' ? 'bg-[#4C90BF]' : 'bg-[#68A25A]'}
      transition-opacity duration-300 ease-in-out flex flex-col gap-2
      -translate-x-1/2
    `}
          style={{ opacity: visible ? 1 : 0 }}
        >
          {status === 'processing' && (
            <div className="flex items-center gap-2">
              <Icons.InfoFill className="w-5 h-5" />
              <span className="font-semibold">
                Batch is processing
                <AnimatedDots />
              </span>
            </div>
          )}

          {status === 'success' && (
            <div className="flex items-center gap-3">
              <Icons.Check className="w-10 h-10" />
              <div className="flex flex-col items-start">
                <span className="font-semibold">Batch processing is done successfully.</span>
                <button
                  type="button"
                  onClick={handleDownload}
                  className="
              mt-2
              px-3
              py-1
              border
              border-white
              rounded
              underline
              text-[12px]
              text-white
              hover:text-gray-300
              hover:border-gray-300
              focus:outline-none
              self-start
              font-semibold
            "
                >
                  Download
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default BatchProcessingNotification;
